import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { ChangeEvent } from 'react';

import type { AtualizarCondutorResult } from '@hooks/condutor/useAtualizarCondutor';
import type { CampoEditavel, DadosCondutor } from '@services/detran/condutor.service';

export interface GestaoCondutorProps {
  cpf: string;
  onCpfChange: (cpf: string) => void;
  onBuscar: () => void;

  dados?: DadosCondutor;
  camposEditaveis: CampoEditavel[];
  buscando: boolean;
  erroBusca: boolean;

  formValues: DadosCondutor;
  onFieldChange: (field: string, value: string | number | boolean) => void;
  onSalvar: () => void;
  salvando: boolean;
  resultado?: AtualizarCondutorResult;
  erroSalvar?: string;

  onRestaurar: () => void;
  restaurando: boolean;
}

const CPF_LENGTH = 11;

const isBooleanField = (campo: CampoEditavel) => campo.type === 'boolean';

export const GestaoCondutor = ({
  cpf,
  onCpfChange,
  onBuscar,
  dados,
  camposEditaveis,
  buscando,
  erroBusca,
  formValues,
  onFieldChange,
  onSalvar,
  salvando,
  resultado,
  erroSalvar,
  onRestaurar,
  restaurando,
}: GestaoCondutorProps) => {
  const handleCpfChange = (event: ChangeEvent<HTMLInputElement>) => {
    onCpfChange(event.target.value.replace(/\D/g, '').slice(0, CPF_LENGTH));
  };

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
      <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
        Gestão DETRAN — Cadastro do Condutor
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Altere um dado do condutor na base do DETRAN. O Perfil do Cidadão passa a refletir a mudança na próxima coleta
        desta secretaria.
      </Typography>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
            <TextField
              label="CPF do condutor"
              value={cpf}
              onChange={handleCpfChange}
              helperText="Somente dígitos"
              fullWidth
            />
            <Button
              variant="contained"
              onClick={onBuscar}
              disabled={cpf.length !== CPF_LENGTH || buscando}
              sx={{ textTransform: 'none', mt: 1, whiteSpace: 'nowrap' }}
            >
              Buscar
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {buscando && (
        <Box display="flex" alignItems="center" sx={{ minHeight: 120 }}>
          <CircularProgress size={24} />
          <Typography sx={{ ml: 2 }}>Consultando a base do DETRAN...</Typography>
        </Box>
      )}

      {erroBusca && !buscando && (
        <Alert severity="error">Não foi possível consultar o condutor na base do DETRAN.</Alert>
      )}

      {dados && !buscando && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
              Dados da habilitação
            </Typography>

            <Grid container spacing={2}>
              {camposEditaveis.map((campo) => (
                <Grid item xs={12} sm={6} key={campo.field}>
                  {isBooleanField(campo) ? (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={Boolean(formValues[campo.field])}
                          onChange={(_event, checked) => onFieldChange(campo.field, checked)}
                        />
                      }
                      label={campo.label}
                    />
                  ) : (
                    <TextField
                      label={campo.label}
                      type={campo.type === 'number' ? 'number' : 'text'}
                      value={formValues[campo.field] ?? ''}
                      onChange={(event) =>
                        onFieldChange(
                          campo.field,
                          campo.type === 'number' ? Number(event.target.value) : event.target.value,
                        )
                      }
                      fullWidth
                    />
                  )}
                </Grid>
              ))}

              {camposEditaveis.length === 0 && (
                <Grid item xs={12}>
                  <Alert severity="info">Nenhum campo editável informado pela base do DETRAN.</Alert>
                </Grid>
              )}
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Button
                variant="contained"
                onClick={onSalvar}
                disabled={salvando || restaurando || camposEditaveis.length === 0}
                sx={{ textTransform: 'none' }}
              >
                {salvando ? 'Salvando...' : 'Salvar alteração'}
              </Button>
              <Button
                variant="outlined"
                onClick={onRestaurar}
                disabled={salvando || restaurando}
                sx={{ textTransform: 'none' }}
              >
                {restaurando ? 'Restaurando...' : 'Restaurar cadastro base'}
              </Button>
            </Stack>

            {erroSalvar && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {erroSalvar}
              </Alert>
            )}

            {resultado && !erroSalvar && (
              <Box sx={{ mt: 2 }}>
                {resultado.changedFields.length === 0 ? (
                  <Alert severity="info">Nenhum campo foi alterado.</Alert>
                ) : (
                  <Alert severity="success">
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Alteração gravada na base do DETRAN. Ela chegará ao Perfil do Cidadão na próxima coleta desta
                      secretaria.
                    </Typography>

                    {resultado.changedFields.map((field) => (
                      <Box key={field} sx={{ mb: 0.5 }}>
                        <Chip size="small" label={field} sx={{ mr: 1 }} />
                        <Typography component="span" variant="body2">
                          {String(resultado.previous[field])} → {String(resultado.current[field])}
                        </Typography>
                      </Box>
                    ))}
                  </Alert>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};
